import { DateTime } from 'luxon'
import { EventVisibility } from '../../types/enums/event-visibility.enum'

/**
 * How far back and ahead the public calendar of a premise reaches, in calendar months. The API never
 * returns a public event outside this window and the timetable cannot page past it (docs/events.md,
 * _Public timetable_).
 */
export const PUBLIC_EVENTS_PAST_MONTHS = 2
export const PUBLIC_EVENTS_AHEAD_MONTHS = 12

/** The longest window one public events request may cover: any three calendar months. */
export const PUBLIC_EVENTS_MAX_RANGE_DAYS = 92

const DAY_MS = 86_400_000

export interface InstantRange {
  start: Date
  end: Date
}

/**
 * The public calendar's window around `now`: from the start of the day `PUBLIC_EVENTS_PAST_MONTHS`
 * back to the end of the day `PUBLIC_EVENTS_AHEAD_MONTHS` ahead, both in the premise's timezone. A day
 * the target month lacks (31 December - 2 months) falls back to that month's last day.
 */
export function publicEventsWindow(now: Date, timezone: string): InstantRange {
  const today = DateTime.fromJSDate(now).setZone(timezone)
  return {
    start: today.minus({ months: PUBLIC_EVENTS_PAST_MONTHS }).startOf('day').toJSDate(),
    end: today.plus({ months: PUBLIC_EVENTS_AHEAD_MONTHS }).endOf('day').toJSDate(),
  }
}

/**
 * The range a public events request asks for, with its defaults filled: no `start` is `now`, no
 * `end` is `PUBLIC_EVENTS_MAX_RANGE_DAYS` after the start. A request with only an `end` is capped to
 * that span too, so no request covers more than it; one with both is checked by the query schema.
 */
export function resolvePublicEventsRange(query: { start?: string; end?: string }, now: Date): InstantRange {
  const start = query.start ? new Date(query.start) : now
  const longest = new Date(start.getTime() + PUBLIC_EVENTS_MAX_RANGE_DAYS * DAY_MS)
  if (!query.end) {
    return { start, end: longest }
  }
  const end = new Date(query.end)
  return { start, end: query.start || end < longest ? end : longest }
}

/**
 * `range` cut to `window`, or `null` when nothing of it is inside. Clamped rather than refused, so a
 * client whose clock is a little off near midnight still gets the part it may see.
 */
export function clampToWindow(range: InstantRange, window: InstantRange): InstantRange | null {
  const start = range.start > window.start ? range.start : window.start
  const end = range.end < window.end ? range.end : window.end
  return start <= end ? { start, end } : null
}

/** Whole days, possibly fractional, between the two ends of `range`. */
export function rangeDays(range: InstantRange): number {
  return (range.end.getTime() - range.start.getTime()) / DAY_MS
}

/**
 * Whether the public page lists an event, on the timetable and the Podujatia cards: `PUBLIC` only. An `UNLISTED`
 * one is reachable by its link (`isReachableByLink`) and never listed (docs/events.md, _Visibility_).
 */
export function isListedPublicly(visibility: readonly EventVisibility[]): boolean {
  return visibility.includes(EventVisibility.PUBLIC)
}

/**
 * Whether the public may open an event by its link and sign up for it, as far as its visibility goes: listed or
 * unlisted. Joining checks this alone and leaves a cancelled or started event to its own refusal, which says why.
 */
export function isOpenByLink(visibility: readonly EventVisibility[]): boolean {
  return isListedPublicly(visibility) || visibility.includes(EventVisibility.UNLISTED)
}

/**
 * Whether a termín's link opens it: open by link (`isOpenByLink`), not cancelled, and inside the public window. The
 * public list's own conditions, with the visibility widened by `UNLISTED`. An ended or locked termín still opens;
 * the page says why it can't be booked.
 */
export function isReachableByLink(
  event: { visibility: readonly EventVisibility[]; cancelledAt: Date | null; start: Date | null; end: Date | null },
  window: InstantRange,
): boolean {
  if (!isOpenByLink(event.visibility) || event.cancelledAt) return false
  const start = event.start ?? event.end
  const end = event.end ?? event.start
  return !!start && !!end && start <= window.end && end >= window.start
}
